import React from 'react';
import { Button, Chip, Flex, Heading, SrOnly, Table, Text } from '@applyboard/crystal-ui';
import { DeleteOutlineIcon, DocumentAlertHifiIcon, EditOutlineIcon } from '@applyboard/ui-icons';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useSearchResult } from '../context';
import type { CourseMappingResource } from '@ab/ams-school-api/types/private/v1';
import { Flag } from '~/components/Flag';
import { EditCourseMappingDialog } from './EditCourseMappingDialog';
import { DeleteCourseMappingDialog } from './DeleteCourseMappingDialog';

export function CourseMappingsTable() {
  const { apiError } = useErrorHandler();
  const { courseMappings, isFetchingCourseMappings, courseMappingsError } = useSearchResult();
  const [editingCourseMapping, setEditingCourseMapping] = React.useState<CourseMappingResource | null>(null);
  const [deletingCourseMapping, setDeletingCourseMapping] = React.useState<CourseMappingResource | null>(null);

  if (courseMappingsError) {
    apiError();
    return null;
  }

  return (
    <Flex direction="column">
      <div role="alert" aria-live="assertive">
        {isFetchingCourseMappings && <SrOnly>Loading course mappings</SrOnly>}
      </div>
      <Table
        data={courseMappings}
        loading={isFetchingCourseMappings}
        overflow="scroll"
        columns={[
          {
            accessorKey: 'attributes.sourceCountry',
            header: 'Country',
            cell: (course: CourseMappingResource) => (
              <Table.Cell>
                {course.attributes.sourceCountry ? (
                  <Flex align="center" gap={2} id={`${course.attributes.sourceCountry.alpha2Code}-country-code`}>
                    <Flag nationalityCode={course.attributes.sourceCountry.alpha2Code} />
                    <Text>{course.attributes.sourceCountry.countryLabel}</Text>
                  </Flex>
                ) : (
                  <Text>-</Text>
                )}
              </Table.Cell>
            ),
          },
          {
            accessorKey: 'attributes.sourceEducationLevel',
            header: 'Source education level',
            cell: (course: CourseMappingResource) => (
              <Table.TextCell>
                {course.attributes.sourceEducationLevelName ?? course.attributes.sourceEducationLevel ?? '-'}
              </Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.sourceInstitutionName',
            header: 'Institution',
            cell: (course: CourseMappingResource) => (
              <Table.TextCell>{course.attributes.sourceInstitutionName ?? '-'}</Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.sourceCourseName',
            header: 'Source course',
            cell: (course: CourseMappingResource) => (
              <Table.TextCell>
                <Chip size="sm">{course.attributes.sourceCourseName}</Chip>
              </Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.equivalentCourse',
            header: 'Equivalent course',
            cell: (course: CourseMappingResource) => (
              <Table.TextCell>
                {course.attributes.equivalentCourseName ?? course.attributes.equivalentCourse}
              </Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.updatedAt',
            header: 'Last modified',
            cell: (course: CourseMappingResource) => (
              <Table.TextCell>{new Date(course.attributes.updatedAt).toLocaleDateString()}</Table.TextCell>
            ),
          },
          {
            accessorKey: 'actions',
            header: () => 'Actions',
            cell: (course: CourseMappingResource) => (
              <Table.TextCell>
                <Flex align="center">
                  <Button
                    intent="secondary"
                    leadIcon={EditOutlineIcon}
                    aria-label="Edit course mapping"
                    emphasis="transparent"
                    onClick={() => setEditingCourseMapping(course)}
                  />
                  <Button
                    intent="negative"
                    leadIcon={DeleteOutlineIcon}
                    aria-label="Delete course mapping"
                    emphasis="transparent"
                    onClick={() => setDeletingCourseMapping(course)}
                  />
                </Flex>
              </Table.TextCell>
            ),
          },
        ]}
      >
        <Table.NoData>
          <Flex align="center" justify="center" direction="column" gap={4} py={32}>
            <DocumentAlertHifiIcon />
            <Heading level={3}>No course mappings found</Heading>
            <Text contrast="mid">Try adjusting your filters to see results.</Text>
          </Flex>
        </Table.NoData>
      </Table>
      {editingCourseMapping && (
        <EditCourseMappingDialog courseMapping={editingCourseMapping} onClose={() => setEditingCourseMapping(null)} />
      )}
      {deletingCourseMapping && (
        <DeleteCourseMappingDialog
          courseMapping={deletingCourseMapping}
          onClose={() => setDeletingCourseMapping(null)}
        />
      )}
    </Flex>
  );
}
